import { buttonVariants } from '@nt/ui/recipes'
import { Component, type ReactNode } from 'react'

import { reportError } from '@/spa/sentry'
import { SUPPORT_EMAIL } from '@/utils/constants'

/**
 * The app-shell error boundary that used to live inline in src/pages/_app.tsx.
 * Reports render crashes to Sentry and shows a manual Reload page / Go to
 * dashboard fallback. The raw error message is logged, never shown: customers
 * get the Sentry event id as a reference support can look up instead.
 * Plain <a href="/"> and a plain <button> on purpose: the router (and anything
 * that depends on it) may be unavailable after a crash, so the fallback wears
 * the system through @nt/ui's class recipes (pure strings: button looks with
 * the focus ring), not through app primitives.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; reference: string | null }
> {
  state = { error: null as Error | null, reference: null as string | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('App crashed:', error)
    const reference = reportError(error, { extra: { componentStack: info.componentStack ?? null } })
    this.setState({ reference: reference || null })
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto my-16 max-w-[720px] px-4 font-sans">
          <div className="rounded-card bg-card p-6 shadow-card">
            <h1 className="mb-2 type-h3 text-foreground">Something went wrong</h1>
            <p className="mb-4 type-body text-body">
              This page could not be shown and we have been told about it. Reload the page to try again. If it keeps
              failing, write to {SUPPORT_EMAIL}.
            </p>
            {this.state.reference ? (
              <p className="type-small text-muted-foreground">
                Reference <code className="font-mono select-all">{this.state.reference}</code>
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') window.location.reload()
                }}
                className={buttonVariants({ variant: 'primary' })}
              >
                Reload page
              </button>
              <a href="/" className={buttonVariants({ variant: 'secondary' })}>
                Go to dashboard
              </a>
            </div>
          </div>
        </div>
      )
    }
    return <>{this.props.children}</>
  }
}
